const isTruthy = (value) => value === true || value === 'true' || value === 1 || value === '1';

export const SURFACE_IDS = ['panel_l3', 'bar_combo', 'bar_like', 'panel_like', 'm4', 'all'];

const hasWidget = (row) => row.combo_widget && row.combo_widget !== 'none';

export const defaultComboWidget = (row) => {
  const name = String(row.field_name || '');
  if (name === 'catalogciiu_levels') return 'ciiu_level';
  if (name.includes('problem')) return 'problem';
  return 'select_id';
};

export const isOnSurface = (row, surfaceId) => {
  if (surfaceId === 'all') return true;
  if (!isTruthy(row.activated) || isTruthy(row.deleted)) return false;
  if (surfaceId === 'panel_l3') {
    return isTruthy(row.use_in_combo) && isTruthy(row.use_in_panel) && hasWidget(row);
  }
  if (surfaceId === 'bar_combo') {
    return isTruthy(row.use_in_combo) && isTruthy(row.use_in_bridge) && hasWidget(row);
  }
  if (surfaceId === 'bar_like') {
    return isTruthy(row.use_in_like) && isTruthy(row.use_in_bridge);
  }
  if (surfaceId === 'panel_like') {
    return isTruthy(row.use_in_like) && isTruthy(row.use_in_panel);
  }
  if (surfaceId === 'm4') {
    return isTruthy(row.use_in_m4);
  }
  return false;
};

const isComboCapable = (row) => {
  const name = String(row.field_name || '');
  const kind = row.source_kind;
  if (name === 'catalogciius_id') return false;
  return kind === 'belongsTo' || kind === 'hasMany' || name.endsWith('_levels') || name.includes('problem');
};

const isLikeCapable = (row) => {
  const kind = row.source_kind;
  return kind === 'column' || kind === 'belongsTo';
};

const isM4Capable = (row) => {
  const name = String(row.field_name || '');
  if (name === 'catalogciiu_levels' || name.endsWith('_levels')) return false;
  if (row.source_kind === 'hasMany') return false;
  return row.source_kind === 'belongsTo' || (name.endsWith('_id') && row.source_kind === 'column');
};

export const isCandidate = (row, surfaceId) => {
  if (surfaceId === 'all') return false;
  if (isTruthy(row.deleted)) return false;
  if (isOnSurface(row, surfaceId)) return false;
  if (surfaceId === 'panel_l3' || surfaceId === 'bar_combo') return isComboCapable(row);
  if (surfaceId === 'bar_like' || surfaceId === 'panel_like') return isLikeCapable(row);
  if (surfaceId === 'm4') return isM4Capable(row);
  return false;
};

export const otherSurfaceIds = (row, currentId) =>
  SURFACE_IDS.filter((id) => id !== 'all' && id !== currentId && isOnSurface(row, id));

const barCombo = (row) => isTruthy(row.use_in_combo) && isTruthy(row.use_in_bridge);
const l3Combo = (row) => isTruthy(row.use_in_combo) && isTruthy(row.use_in_panel);
const barLike = (row) => isTruthy(row.use_in_like) && isTruthy(row.use_in_bridge);
const panelLike = (row) => isTruthy(row.use_in_like) && isTruthy(row.use_in_panel);

export const toggleOffBlockedReason = (row, surfaceId) => {
  if (surfaceId === 'panel_l3' && barCombo(row) && panelLike(row)) return 'sharedPanelAndBar';
  if (surfaceId === 'bar_combo' && l3Combo(row) && barLike(row)) return 'sharedPanelAndBar';
  if (surfaceId === 'bar_like' && panelLike(row) && barCombo(row)) return 'sharedLikeAndCombo';
  if (surfaceId === 'panel_like' && l3Combo(row) && barLike(row)) return 'sharedLikeAndCombo';
  return null;
};

export const buildSurfacePatch = (row, surfaceId, enable) => {
  if (surfaceId === 'all') return null;
  if (surfaceId === 'm4' && enable && String(row.field_name) === 'catalogciiu_levels') return null;
  if (surfaceId === 'bar_combo' && enable && String(row.field_name) === 'catalogciius_id') return null;

  if (enable) {
    const patch = { activated: true, deleted: false };
    if (surfaceId === 'panel_l3') {
      patch.use_in_combo = true;
      patch.use_in_panel = true;
      if (!hasWidget(row)) patch.combo_widget = defaultComboWidget(row);
    } else if (surfaceId === 'bar_combo') {
      patch.use_in_combo = true;
      patch.use_in_bridge = true;
      patch.show_in_bar = true;
      if (!hasWidget(row)) patch.combo_widget = defaultComboWidget(row);
    } else if (surfaceId === 'bar_like') {
      patch.use_in_like = true;
      patch.use_in_bridge = true;
      patch.show_in_bar = true;
    } else if (surfaceId === 'panel_like') {
      patch.use_in_like = true;
      patch.use_in_panel = true;
    } else if (surfaceId === 'm4') {
      patch.use_in_m4 = true;
    }
    return patch;
  }

  if (toggleOffBlockedReason(row, surfaceId)) return null;

  const patch = {};
  if (surfaceId === 'panel_l3') {
    if (barCombo(row)) patch.use_in_panel = false;
    else patch.use_in_combo = false;
  } else if (surfaceId === 'bar_combo') {
    if (l3Combo(row)) {
      patch.use_in_bridge = false;
      patch.show_in_bar = barLike(row);
    } else {
      patch.use_in_combo = false;
    }
  } else if (surfaceId === 'bar_like') {
    if (!panelLike(row)) patch.use_in_like = false;
    if (!barCombo(row)) {
      patch.use_in_bridge = false;
      patch.show_in_bar = false;
    }
  } else if (surfaceId === 'panel_like') {
    if (!barLike(row)) patch.use_in_like = false;
    if (!l3Combo(row)) patch.use_in_panel = false;
  } else if (surfaceId === 'm4') {
    patch.use_in_m4 = false;
  }
  return patch;
};
